import React from 'react';
import { useTranslation } from 'react-i18next';
import { IExperiment, VALIDATION_FIELDS } from '../../types/experiment';

interface ExperimentValidationResultsProps {
    experiment: IExperiment;
    showFullDetails?: boolean;
}

const ExperimentValidationResults: React.FC<ExperimentValidationResultsProps> = ({
    experiment,
    showFullDetails = false
}) => {
    const { t } = useTranslation();

    const getConfidenceColor = (confidence: number): string => {
        if (confidence >= 0.8) return '#22c55e';
        if (confidence >= 0.6) return '#eab308';
        if (confidence >= 0.4) return '#f97316';
        return '#ef4444';
    };

    const formatPercent = (value?: number): string => {
        if (value === undefined || value === null) return '-';
        return `${(value * 100).toFixed(1)}%`;
    };

    const hasValidationData = experiment.confidence_score_distribution ||
        experiment.property_success_rates ||
        experiment.overall_average_confidence !== undefined;

    if (!hasValidationData) {
        return (
            <div className="validation-results empty">
                <span className="material-icons">info</span>
                <p>{t('experiments.validation.noData')}</p>
            </div>
        );
    }

    return (
        <div className="validation-results">
            {/* Overall Summary */}
            <div className="validation-summary">
                <div className="summary-item">
                    <span className="summary-label">{t('experiments.validation.overallConfidence')}</span>
                    <span
                        className="summary-value"
                        style={{ color: getConfidenceColor(experiment.overall_average_confidence || 0) }}
                    >
                        {formatPercent(experiment.overall_average_confidence)}
                    </span>
                </div>
                {experiment.overall_validation_rate !== undefined && (
                    <div className="summary-item">
                        <span className="summary-label">{t('experiments.validation.validationRate')}</span>
                        <span className="summary-value">
                            {formatPercent(experiment.overall_validation_rate)}
                        </span>
                    </div>
                )}
                {experiment.validation_coverage !== undefined && (
                    <div className="summary-item">
                        <span className="summary-label">{t('experiments.validation.coverage')}</span>
                        <span className="summary-value">
                            {formatPercent(experiment.validation_coverage)}
                        </span>
                    </div>
                )}
            </div>

            {/* Validation Statistics */}
            {(experiment.total_experiments_validated !== undefined ||
              experiment.total_experiments_passed_validation !== undefined) && (
                <div className="validation-stats">
                    <h4>{t('experiments.validation.statistics')}</h4>
                    <div className="stats-grid">
                        {experiment.total_experiments_in_storage !== undefined && (
                            <div className="stat-item">
                                <span className="stat-value">{experiment.total_experiments_in_storage}</span>
                                <span className="stat-label">{t('experiments.validation.totalInStorage')}</span>
                            </div>
                        )}
                        {experiment.total_experiments_validated !== undefined && (
                            <div className="stat-item">
                                <span className="stat-value">{experiment.total_experiments_validated}</span>
                                <span className="stat-label">{t('experiments.validation.totalValidated')}</span>
                            </div>
                        )}
                        {experiment.total_experiments_passed_validation !== undefined && (
                            <div className="stat-item success">
                                <span className="stat-value">{experiment.total_experiments_passed_validation}</span>
                                <span className="stat-label">{t('experiments.validation.passed')}</span>
                            </div>
                        )}
                        {experiment.total_experiments_failed_validation !== undefined && (
                            <div className="stat-item error">
                                <span className="stat-value">{experiment.total_experiments_failed_validation}</span>
                                <span className="stat-label">{t('experiments.validation.failed')}</span>
                            </div>
                        )}
                        {experiment.total_experiments_with_errors !== undefined && (
                            <div className="stat-item warning">
                                <span className="stat-value">{experiment.total_experiments_with_errors}</span>
                                <span className="stat-label">{t('experiments.validation.withErrors')}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Field-by-Field Results */}
            {showFullDetails && (experiment.property_success_rates || experiment.confidence_score_distribution) && (
                <div className="validation-fields">
                    <h4>{t('experiments.validation.fieldResults')}</h4>
                    <table className="validation-fields-table">
                        <thead>
                            <tr>
                                <th>{t('experiments.validation.field')}</th>
                                <th>{t('experiments.validation.successRate')}</th>
                                <th>{t('experiments.validation.avgConfidence')}</th>
                                <th>{t('experiments.validation.found')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {VALIDATION_FIELDS.map((field) => {
                                const successRate = experiment.property_success_rates?.[field as keyof typeof experiment.property_success_rates];
                                const avgConfidence = experiment.property_average_confidence?.[field as keyof typeof experiment.property_average_confidence];
                                const foundCount = experiment.property_found_counts?.[field as keyof typeof experiment.property_found_counts];
                                const totalCount = experiment.property_total_counts?.[field as keyof typeof experiment.property_total_counts];

                                return (
                                    <tr key={field}>
                                        <td className="field-name">
                                            <span className="material-icons field-icon">
                                                {field === 'title' ? 'title' :
                                                 field === 'author' ? 'person' :
                                                 field === 'date' ? 'calendar_today' :
                                                 field === 'keywords' ? 'label' :
                                                 field === 'subject' ? 'subject' :
                                                 field === 'document_type' ? 'category' : 'info'}
                                            </span>
                                            {t(`experiments.validation.fields.${field}`) || field}
                                        </td>
                                        <td>
                                            {successRate !== undefined ? (
                                                <span
                                                    className="success-rate"
                                                    style={{ color: getConfidenceColor(successRate) }}
                                                >
                                                    {formatPercent(successRate)}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {avgConfidence !== undefined ? (
                                                <span
                                                    className="avg-confidence"
                                                    style={{ color: getConfidenceColor(avgConfidence) }}
                                                >
                                                    {formatPercent(avgConfidence)}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {foundCount !== undefined && totalCount !== undefined
                                                ? `${foundCount}/${totalCount}`
                                                : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Confidence Distribution */}
            {showFullDetails && experiment.confidence_score_distribution?.overall && (
                <div className="validation-distribution">
                    <h4>{t('experiments.validation.confidenceDistribution')}</h4>
                    <div className="distribution-stats">
                        <div className="distribution-item">
                            <span className="distribution-label">{t('experiments.validation.min')}</span>
                            <span className="distribution-value">
                                {formatPercent(experiment.confidence_score_distribution.overall.min)}
                            </span>
                        </div>
                        <div className="distribution-item">
                            <span className="distribution-label">{t('experiments.validation.max')}</span>
                            <span className="distribution-value">
                                {formatPercent(experiment.confidence_score_distribution.overall.max)}
                            </span>
                        </div>
                        <div className="distribution-item">
                            <span className="distribution-label">{t('experiments.validation.average')}</span>
                            <span className="distribution-value">
                                {formatPercent(experiment.confidence_score_distribution.overall.average)}
                            </span>
                        </div>
                        <div className="distribution-item">
                            <span className="distribution-label">{t('experiments.validation.count')}</span>
                            <span className="distribution-value">
                                {experiment.confidence_score_distribution.overall.count}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Validation Metadata */}
            {showFullDetails && (experiment.validation_engine_version || experiment.validation_last_updated) && (
                <div className="validation-metadata">
                    <h4>{t('experiments.validation.metadata')}</h4>
                    <div className="metadata-grid">
                        {experiment.validation_engine_version && (
                            <div className="metadata-item">
                                <span className="metadata-label">{t('experiments.validation.engineVersion')}</span>
                                <span className="metadata-value">{experiment.validation_engine_version}</span>
                            </div>
                        )}
                        {experiment.validation_data_source && (
                            <div className="metadata-item">
                                <span className="metadata-label">{t('experiments.validation.dataSource')}</span>
                                <span className="metadata-value">{experiment.validation_data_source}</span>
                            </div>
                        )}
                        {experiment.validation_batch_size && (
                            <div className="metadata-item">
                                <span className="metadata-label">{t('experiments.validation.batchSize')}</span>
                                <span className="metadata-value">{experiment.validation_batch_size}</span>
                            </div>
                        )}
                        {experiment.validation_last_updated && (
                            <div className="metadata-item">
                                <span className="metadata-label">{t('experiments.validation.lastUpdated')}</span>
                                <span className="metadata-value">
                                    {new Date(experiment.validation_last_updated).toLocaleString()}
                                </span>
                            </div>
                        )}
                        {experiment.validation_note && (
                            <div className="metadata-item full-width">
                                <span className="metadata-label">{t('experiments.validation.note')}</span>
                                <span className="metadata-value">{experiment.validation_note}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExperimentValidationResults;
